import { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MapLocationButton } from '../components/MapLocationButton';
import { Dumbbell, MapPin, Phone, ExternalLink, Search, Calendar, Clock, MapPinned } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Types to exclude from sports centers
const EXCLUDED_CENTER_TYPES = ['Administracion Publica Juzgado'];

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const sportsIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count >= 100 ? 50 : count >= 10 ? 40 : 30;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);background-color:#22c55e;">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

export function SportsPage() {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const t = {
    title: language === 'es' ? 'Deportes' : 'Sports',
    subtitle: language === 'es' ? 'Eventos deportivos y centros de ocio' : 'Sports events and leisure centers',
    eventsTab: language === 'es' ? 'Agenda de Eventos' : 'Events Calendar',
    centersTab: language === 'es' ? 'Centros Deportivos y de Ocio' : 'Sports & Leisure Centers',
    searchPlaceholder: language === 'es' ? 'Buscar...' : 'Search...',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    of: language === 'es' ? 'de' : 'of',
    results: language === 'es' ? 'resultados' : 'results',
    allTypes: language === 'es' ? 'Todos' : 'All',
    filterByType: language === 'es' ? 'Filtrar por tipo' : 'Filter by type',
    openInGoogleMaps: language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps',
    loading: language === 'es' ? 'Cargando...' : 'Loading...',
    eventsNote: language === 'es' ? 'Solo se muestran los eventos del último mes' : 'Only showing events from the last month',
    events: language === 'es' ? 'eventos' : 'events',
    centers: language === 'es' ? 'centros' : 'centers',
    moreInfo: language === 'es' ? 'Más información' : 'More info',
    location: language === 'es' ? 'Lugar' : 'Location',
    organizer: language === 'es' ? 'Organizador' : 'Organizer',
  };

  // Fetch events on initial load - only last month
  useEffect(() => {
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        const response = await axios.get(`${API}/sports/events`);
        const allEvents = response.data || [];
        
        // Filter events from the last month
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        const recentEvents = allEvents.filter(event => {
          if (!event.fecha_inicio) return false;
          const eventDate = new Date(event.fecha_inicio);
          return eventDate >= oneMonthAgo;
        });
        
        setEvents(recentEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoadingEvents(false);
      }
    };
    fetchEvents();
  }, []);

  // Fetch centers when tab changes (excluding unwanted types)
  useEffect(() => {
    if (activeTab === 'centers' && centers.length === 0) {
      const fetchCenters = async () => {
        setLoadingCenters(true);
        try {
          const response = await axios.get(`${API}/sports/centers`);
          const allCenters = response.data || [];
          // Filter out excluded types
          const filteredCenters = allCenters.filter(c => 
            !EXCLUDED_CENTER_TYPES.includes(c.tipo)
          );
          setCenters(filteredCenters);
        } catch (error) {
          console.error('Error fetching centers:', error);
        } finally {
          setLoadingCenters(false);
        }
      };
      fetchCenters();
    }
  }, [activeTab, centers.length]);

  // Get unique types for centers filter
  const centerTypes = useMemo(() => {
    const types = new Set();
    centers.forEach(c => {
      if (c.tipo) types.add(c.tipo);
    });
    return Array.from(types).sort();
  }, [centers]);

  // Filter centers
  const filteredCenters = useMemo(() => {
    let filtered = centers;
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(c => c.tipo === typeFilter);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(c =>
        (c.nombre || '').toLowerCase().includes(searchLower) ||
        (c.municipio || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [centers, typeFilter, search]);

  // Filter events
  const filteredEvents = useMemo(() => {
    if (!search) return events;
    const searchLower = search.toLowerCase();
    return events.filter(e =>
      (e.nombre || '').toLowerCase().includes(searchLower) ||
      (e.lugar || '').toLowerCase().includes(searchLower) ||
      (e.municipio || '').toLowerCase().includes(searchLower)
    );
  }, [events, search]);

  const markers = useMemo(() => 
    filteredCenters.filter(c => c.lat && c.lon),
    [filteredCenters]
  );

  const centerPosition = [28.2916, -16.5291];

  const openInGoogleMaps = (lat, lon) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Dumbbell className="w-5 h-5 text-green-600" />
          <span className="text-sm font-medium text-green-600 uppercase tracking-wider">
            {language === 'es' ? 'Datos Abiertos' : 'Open Data'}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2" data-testid="sports-title">
          {t.title}
        </h1>
        <p className="text-lg text-[#252a34]/70">{t.subtitle}</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 h-12">
          <TabsTrigger value="events" className="text-sm font-medium">
            <Calendar className="w-4 h-4 mr-2" />
            {t.eventsTab}
          </TabsTrigger>
          <TabsTrigger value="centers" className="text-sm font-medium">
            <MapPinned className="w-4 h-4 mr-2" />
            {t.centersTab}
          </TabsTrigger>
        </TabsList>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-4">
          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200">
              <span className="text-2xl font-bold text-green-600">{events.length}</span>
              <span className="text-gray-600 ml-2">{t.events}</span>
            </div>
            <p className="text-sm text-[#252a34]/60 italic">{t.eventsNote}</p>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
            <Input
              type="text"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 bg-white border-slate-200"
            />
          </div>

          {/* Events List */}
          {loadingEvents ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredEvents.map((event, idx) => (
                <Card key={idx} className="overflow-hidden border-slate-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-[#252a34] mb-2 line-clamp-2">{event.nombre}</h3>
                    
                    <div className="space-y-2 text-sm text-[#252a34]/70">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-green-600" />
                        <span>{formatDate(event.fecha_inicio)}</span>
                      </div>
                      
                      {event.lugar && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-green-600" />
                          <span>{event.lugar}</span>
                        </div>
                      )}
                      
                      {event.municipio && (
                        <Badge variant="outline" className="text-xs">
                          {event.municipio}
                        </Badge>
                      )}
                      
                      {event.organizador && (
                        <p className="text-xs text-[#252a34]/50">
                          {t.organizer}: {event.organizador}
                        </p>
                      )}
                    </div>
                    
                    {event.url && (
                      <a 
                        href={event.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-green-600 hover:text-green-800 mt-3"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {t.moreInfo}
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Centers Tab */}
        <TabsContent value="centers" className="space-y-4">
          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200">
              <span className="text-2xl font-bold text-green-600">{centers.length.toLocaleString()}</span>
              <span className="text-gray-600 ml-2">{t.centers}</span>
            </div>
          </div>

          {/* Type Filters */}
          {!loadingCenters && centers.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-[#252a34]/60 mb-2">{t.filterByType}:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setTypeFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    typeFilter === 'all'
                      ? 'bg-green-600 text-white'
                      : 'bg-white text-[#252a34] border border-slate-200 hover:border-green-400'
                  }`}
                >
                  {t.allTypes} ({centers.length.toLocaleString()})
                </button>
                {centerTypes.slice(0, 10).map((tipo) => {
                  const count = centers.filter(c => c.tipo === tipo).length;
                  const isActive = typeFilter === tipo;
                  return (
                    <button
                      key={tipo}
                      onClick={() => setTypeFilter(tipo)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isActive
                          ? 'bg-green-600 text-white'
                          : 'bg-white text-[#252a34] border border-slate-200 hover:shadow-sm'
                      }`}
                    >
                      {tipo} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search */}
          {!loadingCenters && (
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
                <Input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-12 bg-white border-slate-200"
                />
              </div>
              <div className="text-sm text-[#252a34]/60 flex items-center gap-2">
                {t.showing} <strong className="text-green-600">{filteredCenters.length.toLocaleString()}</strong> {t.of} {centers.length.toLocaleString()} {t.results}
              </div>
            </div>
          )}

          {/* Map */}
          <Card className="overflow-hidden border-slate-200">
            <div className="h-[400px] md:h-[450px]">
              {loadingCenters ? (
                <div className="flex items-center justify-center h-full bg-slate-50">
                  <Spinner size="lg" />
                </div>
              ) : (
                <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapLocationButton language={language} />
                  <MarkerClusterGroup 
                    chunkedLoading 
                    iconCreateFunction={createClusterIcon} 
                    maxClusterRadius={50}
                  >
                    {markers.map((item, idx) => (
                      <Marker 
                        key={idx} 
                        position={[item.lat, item.lon]} 
                        icon={sportsIcon}
                      >
                        <Popup>
                          <div className="font-['Outfit'] min-w-[200px] max-w-[280px]">
                            <h3 className="font-bold text-[#252a34] mb-1 text-sm">{item.nombre}</h3>
                            <Badge className="text-xs mb-2 bg-green-600 text-white">
                              {item.tipo}
                            </Badge>
                            <div className="text-xs text-[#252a34]/70 space-y-1 mt-2">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span>{item.municipio}</span>
                              </div>
                              {item.direccion && (
                                <p className="text-[#252a34]/60 ml-4">{item.direccion}</p>
                              )}
                              {item.telefono && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3 flex-shrink-0" />
                                  <a href={`tel:${item.telefono}`} className="text-[#00599c] hover:underline">
                                    {item.telefono}
                                  </a>
                                </div>
                              )}
                            </div>
                            <button 
                              onClick={() => openInGoogleMaps(item.lat, item.lon)}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-3"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {t.openInGoogleMaps}
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MarkerClusterGroup>
                </MapContainer>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
